import { useCallback, useRef, useState } from 'react';
import dashboardRequest from '@/pages/dashboard/utils/dashboardRequest';
import {
  IFilterServiceConfig,
  resolveFilterServiceParams,
  resolveFilterServiceResponse,
} from '../defaultServiceConfig';
import { getCOSPresigned } from '@/pages/aigc/pages/Agent/service/service';
import {
  AnomalyReportUrlSigner,
  IAnomalyReportItem,
  signAnomalyReportListUrls,
} from '../utils';

export interface IAnomalyReportListState {
  loading: boolean;
  data: IAnomalyReportItem[] | null;
  errorMsg: string;
}

const INIT_STATE: IAnomalyReportListState = {
  loading: false,
  data: null,
  errorMsg: '',
};

export interface IFetchReportListResult {
  list: IAnomalyReportItem[] | null;
  errorMsg: string;
}

const getAnomalyReportCOSPresigned: AnomalyReportUrlSigner = data =>
  getCOSPresigned(data) as Promise<{ url?: string[]; }>;

export const useAnomalyReportList = (
  serviceConfig: IFilterServiceConfig,
  pageData: Record<string, any> = {},
) => {
  const [reportState, setReportState] =
    useState<IAnomalyReportListState>(INIT_STATE);
  const requestSeqRef = useRef(0);

  const fetchReportList = useCallback(
    async (
      filterVal: Record<string, any>,
    ): Promise<IFetchReportListResult> => {
      const { api } = serviceConfig;

      if (!api) {
        const errorMsg = 'api config error';
        setReportState({
          loading: false,
          data: null,
          errorMsg,
        });
        return { list: null, errorMsg };
      }

      const requestData = {
        ...pageData,
        filterVal,
      };
      const params = resolveFilterServiceParams(serviceConfig, requestData);

      if (params === false) {
        return { list: null, errorMsg: '' };
      }

      const requestSeq = requestSeqRef.current + 1;
      requestSeqRef.current = requestSeq;

      setReportState({
        loading: true,
        data: null,
        errorMsg: '',
      });

      try {
        let resultPromise: Promise<any> | null = null;

        if (typeof api === 'string') {
          resultPromise = dashboardRequest({
            url: api,
            method: 'post',
            data: params,
          });
        } else if (typeof api === 'function') {
          resultPromise = api(params, requestData);
        }

        if (!resultPromise) {
          const errorMsg = 'api config error';
          setReportState({
            loading: false,
            data: null,
            errorMsg,
          });
          return { list: null, errorMsg };
        }

        const res = await resultPromise;

        if (requestSeqRef.current !== requestSeq) {
          return { list: null, errorMsg: '' };
        }

        const formattedData = resolveFilterServiceResponse(
          serviceConfig,
          res,
          filterVal,
        );
        const list = (
          Array.isArray(formattedData) ? formattedData : []
        ) as IAnomalyReportItem[];
        const signedList = await signAnomalyReportListUrls(
          list,
          getAnomalyReportCOSPresigned,
        );

        if (requestSeqRef.current !== requestSeq) {
          return { list: null, errorMsg: '' };
        }

        setReportState({
          loading: false,
          data: signedList,
          errorMsg: '',
        });
        return { list: signedList, errorMsg: '' };
      } catch (error: any) {
        if (requestSeqRef.current !== requestSeq) {
          return { list: null, errorMsg: '' };
        }

        const errorMsg = error?.msg || 'api config error';
        setReportState({
          loading: false,
          data: null,
          errorMsg,
        });
        return { list: null, errorMsg };
      }
    },
    [pageData, serviceConfig],
  );

  return {
    reportState,
    fetchReportList,
  };
};
